import React from "react";
import dayjs from "dayjs";
import { formatINR } from "@/utils/goldSilverParser";
import { DecreaseArrowIcon, IncreaseArrowIcon } from "./Icons";

const GOLD_ROWS = [
    { caratKey: "24", label: "24 कैरेट" },
    { caratKey: "22", label: "22 कैरेट" },
    { caratKey: "18", label: "18 कैरेट" },
    { caratKey: "14", label: "14 कैरेट" },
];

const SILVER_ROWS = [
    { weightKey: "oneGram", label: "1 ग्राम" },
    { weightKey: "tenGram", label: "10 ग्राम" },
    { weightKey: "hundredGram", label: "100 ग्राम" },
    { weightKey: "oneKg", label: "1000 ग्राम" },
];

function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function yesterdayPrice(today, diff, trend) {
    if (today == null || diff == null) return null;
    return trend === "increase" ? today - diff : today + diff;
}

function bgClassToCss(cls, fallback = "#ffffff") {
    if (!cls) return fallback;
    const match = cls.match(/^bg-\[#([0-9a-fA-F]{3,8})\](?:\/(\d{1,3}))?$/);
    if (!match) return fallback;
    let hex = match[1];
    let alpha = match[2] != null ? Number(match[2]) / 100 : 1;
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    if (hex.length === 8) {
        alpha = parseInt(hex.slice(6, 8), 16) / 255;
        hex = hex.slice(0, 6);
    }
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function extractHex(cls, fallback) {
    if (!cls) return fallback;
    const match = cls.match(/#([0-9a-fA-F]{3,8})/);
    if (!match) return fallback;
    let hex = match[1];
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    return `#${hex.slice(0, 6)}`;
}

function stickyFillStyle(tintColor, extraShadow, borderColor) {
    const shadows = [extraShadow, `inset 0 -1px 0 0 ${borderColor}`, `inset 0 0 0 9999px ${tintColor}`].filter(Boolean);
    return {
        backgroundColor: "#ffffff",
        boxShadow: shadows.join(", "),
        transform: "translateZ(0)",
    };
}

function DiffText({ isUp, amount, isGold = true }) {
    if (amount == null) return "-";
    const isZero = amount === 0;
    return (
        <span
            className={`inline-flex items-center justify-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${isZero
                ? (isGold ? "bg-yellow-100 text-black" : "bg-gray-200 text-black")
                : isUp
                    ? "bg-green-100 text-green-600"
                    : "bg-red-100 text-red-600"
                }`}
            style={{ minWidth: "64px" }}
        >
            {isZero ? null : isUp ? <IncreaseArrowIcon /> : <DecreaseArrowIcon />}
            {isUp ? "+" : "-"}₹{Math.abs(amount)}
        </span>
    );
}

export const CurrentRatesTable = ({ data, type = "gold", colors = {}  }) => {
    const isGold = type === "gold";
    const yesterdayLabel = dayjs().subtract(1, "day").format("DD MMM");

    return (
        <div>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className={colors.lightBg || "bg-[#fafafa]"}>
                        <tr>
                            <th
                                className="sticky left-0 z-20 px-2 py-3 text-xs md:text-sm font-semibold whitespace-nowrap text-left will-change-transform"
                                style={stickyFillStyle(bgClassToCss(colors.lightBg, "#fafafa"), "2px 0 4px -2px rgba(0,0,0,0.15)", extractHex(colors.border, "#e5e7eb"))}
                            >
                                {isGold ? "शुद्धता" : "Weight"}
                            </th>
                            {isGold ? (
                                <>
                                    <th className={`px-2 py-3 text-xs md:text-sm font-semibold border-b ${colors.border || "border-gray-200"} whitespace-nowrap`}>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", alignItems: "center", columnGap: "6px" }}>
                                            <span className="text-right">1 ग्राम</span>
                                            <span />
                                        </div>
                                    </th>
                                    <th className={`px-2 py-3 text-xs md:text-sm font-semibold border-b ${colors.border || "border-gray-200"} whitespace-nowrap`}>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", alignItems: "center", columnGap: "6px" }}>
                                            <span className="text-right">8 ग्राम</span>
                                            <span />
                                        </div>
                                    </th>
                                    <th className={`px-2 py-3 text-xs md:text-sm font-semibold border-b ${colors.border || "border-gray-200"} whitespace-nowrap`}>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", alignItems: "center", columnGap: "6px" }}>
                                            <span className="text-right">10 ग्राम</span>
                                            <span />
                                        </div>
                                    </th>
                                </>
                            ) : (
                                <>
                                    <th className={`px-2 py-3 text-xs md:text-sm font-semibold border-b ${colors.border || "border-gray-200"} whitespace-nowrap text-center`}>आज</th>
                                    <th className={`px-2 py-3 text-xs md:text-sm font-semibold border-b ${colors.border || "border-gray-200"} whitespace-nowrap text-center`}>{`कल (${yesterdayLabel})`}</th>
                                    <th className={`px-2 py-3 text-xs md:text-sm font-semibold border-b ${colors.border || "border-gray-200"} whitespace-nowrap text-center`}>बदलाव</th>
                                </>
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {isGold
                            ? GOLD_ROWS.map((row, index) => (
                                <tr key={row.caratKey} className={`group border-b ${colors.border || "border-gray-200"} last:border-none hover:bg-gray-50`}>
                                    <td
                                        className="sticky left-0 z-10 px-2 py-3 whitespace-nowrap text-xs md:text-sm text-left font-bold will-change-transform group-hover:[--row-bg:#f9fafb]"
                                        style={stickyFillStyle("var(--row-bg, #ffffff)", "2px 0 4px -2px rgba(0,0,0,0.15)", index === GOLD_ROWS.length - 1 ? "transparent" : extractHex(colors.border, "#e5e7eb"))}
                                    >
                                        {row.label}
                                    </td>
                                    <td className="px-2 py-3 whitespace-nowrap text-xs md:text-sm">
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", alignItems: "center", columnGap: "6px" }}>
                                            <span className="text-right">{formatINR(data?.oneGram?.[`carat${row.caratKey}`])}</span>
                                            <span className="justify-self-start">
                                                <DiffText
                                                    isUp={data?.[`trend${row.caratKey}`] === "increase"}
                                                    amount={data?.[`difference${row.caratKey}`]}
                                                />
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-2 py-3 whitespace-nowrap text-xs md:text-sm">
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", alignItems: "center", columnGap: "6px" }}>
                                            <span className="text-right">{formatINR(data?.eightGram?.[`carat${row.caratKey}`])}</span>
                                            <span className="justify-self-start">
                                                <DiffText
                                                    isUp={data?.[`trend${row.caratKey}`] === "increase"}
                                                    amount={data?.[`difference${row.caratKey}`] != null ? data[`difference${row.caratKey}`] * 8 : null}
                                                />
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-2 py-3 whitespace-nowrap text-xs md:text-sm">
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", alignItems: "center", columnGap: "6px" }}>
                                            <span className="text-right">{formatINR(data?.tenGram?.[`carat${row.caratKey}`])}</span>
                                            <span className="justify-self-start">
                                                <DiffText
                                                    isUp={data?.[`trend${row.caratKey}`] === "increase"}
                                                    amount={data?.[`difference${row.caratKey}`] != null ? data[`difference${row.caratKey}`] * 10 : null}
                                                />
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))
                            : SILVER_ROWS.map((row, index) => {
                                const today = data?.[row.weightKey];
                                const diff = data?.[`difference${capitalize(row.weightKey)}`];
                                const trend = data?.[`trend${capitalize(row.weightKey)}`];
                                const yesterday = yesterdayPrice(today, diff, trend);
                                return (
                                    <tr key={row.weightKey} className={`group border-b ${colors.border || "border-gray-200"} last:border-none hover:bg-gray-50`}>
                                        <td
                                            className="sticky left-0 z-10 px-2 py-3 whitespace-nowrap text-xs md:text-sm text-left font-bold will-change-transform group-hover:[--row-bg:#f9fafb]"
                                            style={stickyFillStyle("var(--row-bg, #ffffff)", "2px 0 4px -2px rgba(0,0,0,0.15)", index === SILVER_ROWS.length - 1 ? "transparent" : extractHex(colors.border, "#e5e7eb"))}
                                        >
                                            {row.label}
                                        </td>
                                        <td className="px-2 py-3 whitespace-nowrap text-xs md:text-sm text-center  ">
                                            {formatINR(today)}
                                        </td>
                                        <td className="px-2 py-3 whitespace-nowrap text-xs md:text-sm text-center  ">
                                            {formatINR(yesterday)}
                                        </td>
                                        <td className="px-2 py-3 whitespace-nowrap text-center">
                                            <DiffText isUp={trend === "increase"} amount={diff} isGold={false}/>
                                        </td>
                                    </tr>
                                );
                            })}

                        {!data && (
                            <tr>
                                <td colSpan={isGold ? 5 : 4} className="px-4 py-6 text-center text-sm text-gray-400">
                                    कोई डेटा उपलब्ध नहीं है
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>


        </div>
    );
};
